import PropTypes from 'prop-types'
import { useDrag, useDrop } from 'react-dnd'
import { useFieldArray } from 'react-hook-form'

import { cn } from '@/utils/helpers'

function AnswerInput({ question, onDrop, value, name }) {
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: `answer-${question.id}`,
    drop: (val) => onDrop(val),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }))
  const isActive = isOver && canDrop

  return (
    <button
      ref={drop}
      type="button"
      name={name}
      className={cn(
        'border-input mx-2 min-w-20 rounded border-[1.5px] px-2',
        value === '' && 'border-dashed',
        isActive ? 'border-primary bg-primary/15' : 'bg-secondary',
      )}
    >
      {value === '' ? <>&nbsp;</> : value}
    </button>
  )
}
AnswerInput.propTypes = {
  question: PropTypes.object.isRequired,
  onDrop: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
}

function AnswerBlock({ question, option }) {
  const [{ opacity }, drag] = useDrag(
    () => ({
      type: `answer-${question.id}`,
      item: { title: option.title },
      collect: (monitor) => ({
        opacity: monitor.isDragging() ? 0.4 : 1,
      }),
      canDrag: !option.used,
    }),
    [question, option],
  )
  return (
    <button
      type="button"
      ref={drag}
      disabled={option.used}
      className={cn(
        'bg-secondary border-input rounded border-[1.5px] px-2 text-2xl',
        option.used ? 'text-muted-foreground' : 'text-foreground hover:bg-primary/20 cursor-move',
      )}
      style={{ opacity }}
    >
      {option.title}
    </button>
  )
}
AnswerBlock.propTypes = {
  question: PropTypes.object.isRequired,
  option: PropTypes.shape({
    title: PropTypes.string.isRequired,
    used: PropTypes.bool.isRequired,
  }).isRequired,
}

function DragAndDropQuestion({ question, field, control }) {
  const answers = question.answers.answers.map((a) => ({
    title: a,
    used: field.value?.some((f) => f.value === a),
  }))

  // Map [null, "word", null] to [0, "word", 1] so we can track which field to update
  const { templateToAnswerArray } = question.answers.template.reduce(
    (acc, curr) => {
      if (curr === null) {
        acc.templateToAnswerArray.push(acc.index)
        acc.index += 1
      } else acc.templateToAnswerArray.push('phrase')
      return acc
    },
    { index: 0, templateToAnswerArray: [] },
  )

  // We don't use fields, as we are looping through the question.answers.template instead
  const { update } = useFieldArray({
    control,
    name: field.name,
  })

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm italic">
        Drag each answer into a blank to complete the question.
      </p>
      <div className="flex flex-col justify-between gap-4 lg:flex-row">
        <p className="line max-w-lg text-2xl">
          {question.answers.template.map((answer, index) => {
            if (answer === null) {
              const fieldIndex = templateToAnswerArray[index]
              return (
                <AnswerInput
                  question={question}
                  key={`question.${fieldIndex}.value`}
                  name={`question.${fieldIndex}.value`}
                  value={field?.value?.[fieldIndex]?.value || ''}
                  onDrop={({ title }) => {
                    update(fieldIndex, { value: title })
                  }}
                />
              )
            }
            // Template is static, and may contain duplicate answer strings
            // eslint-disable-next-line react/no-array-index-key
            return <span key={`${answer}-${index}`}>{answer}</span>
          })}
        </p>
        <ul className="flex flex-row flex-wrap gap-2 lg:flex-col">
          {answers.map((option) => (
            <li key={option.title}>
              <AnswerBlock question={question} option={option} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default DragAndDropQuestion

DragAndDropQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  field: PropTypes.object.isRequired,
  control: PropTypes.object.isRequired,
}
